import React from 'react';
import Header from '../Header';
import Footer from '../Footer';
import useRouteMeta from '../../../hooks/useRouteMeta';
import './TermsPage.css';
import FlutterServicesAPI from '../../../services/flutterServices';
import { useNavigate } from 'react-router-dom';
import { IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { merchantAuthAPI } from '../../../services/merchantAPI';

const TermsPage = () => {
  // SEO Meta Tags - Automatically applied from config
  useRouteMeta();
  const navigate = useNavigate();
  const [showWhatsAppSmsAuth, setShowWhatsAppSmsAuth] = React.useState(true);

  React.useEffect(() => {
    const checkAuthVisibility = async () => {
      try {
        const response = await merchantAuthAPI.checkAuthVisibility();
        if (response.data && typeof response.data.show_whatsapp_sms_auth === 'boolean') {
          setShowWhatsAppSmsAuth(response.data.show_whatsapp_sms_auth);
        }
      } catch (error) {
        console.error('❌ Failed to check auth visibility:', error);
      }
    };
    checkAuthVisibility();
  }, []);

  return (
    <div className="privacy-page">
      <Header />

      <div className="container">
        <div className="row">
          <div className="col-md-12 privacy-page-content page-header-container">
            <div className="back-btn-container">
              <IconButton onClick={() => navigate(-1)} color="inherit" sx={{ fontSize: { xs: '15px', md: '17px' }, padding: { xs: '5px 15px', md: '7px 15px' } }} style={{ border: '1px solid #000000', color: '#000000', borderRadius: '33px', fontFamily: 'var(--font-montserrat-medium)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '2px' }}>
                <ArrowBackIcon fontSize='1.2rem' /> Back
              </IconButton>
            </div>
            <h1>Glammify Terms of Service</h1>
          </div>
        </div>
        <div className="row justify-content-center">
          <div className="col-md-12 privacy-page-content-2">
            <div className='row'>

              {/* Intro */}
              <div className='col-md-12 legal-text'>
                <p>These Terms of Service ("Terms") govern your access to and use of Glammify's website, mobile applications, AI-powered tools, and related services (collectively, the "Services").</p>
                <p>By creating an account, uploading content, or using our Services, you agree to be bound by these Terms. If you do not agree to these Terms, you may not use the Services.</p>
              </div>

              {/* 1. Eligibility */}
              <div className='col-md-12 legal-text'>
                <h3>1. Eligibility</h3>
                <p>You must be at least 18 years old (or the age of majority in your jurisdiction) to use the Services.</p>
                <p>By using Glammify, you represent and warrant that:</p>
                <ul>
                  <li>You have the legal capacity to enter into these Terms.</li>
                  <li>You have the authority to bind the entity you represent, if using the Services on behalf of a business.</li>
                </ul>
              </div>

              {/* 2. User Accounts */}
              <div className='col-md-12 legal-text'>
                <h3>2. User Accounts</h3>
                <p>You are responsible for maintaining the confidentiality of your account credentials.</p>
                <p>You agree to:</p>
                <ul>
                  <li>Provide accurate and complete information when creating your account.</li>
                  <li>Keep your account information updated.</li>
                  <li>Be responsible for all activities conducted through your account.</li>
                </ul>
                <p>Glammify reserves the right to suspend or terminate accounts that violate these Terms or misuse the Services.</p>
              </div>

              {/* 3. User-Uploaded Content */}
              <div className='col-md-12 legal-text'>
                <h3>3. User-Uploaded Content</h3>

                <h6>a. Ownership of Content</h6>
                <p>You retain ownership of the jewelry designs, photographs, product media, and other content you upload to the platform ("User Content").</p>
                <p>By uploading User Content, you represent and warrant that:</p>
                <ul>
                  <li>You are the legal owner of the content or have all necessary rights, licenses, and permissions to upload and use it.</li>
                  <li>Your content does not infringe intellectual property rights, privacy rights, publicity rights, or other rights of any third party.</li>
                </ul>

                <h6>b. License to Glammify (Service Operation)</h6>
                <p>By uploading User Content, you grant Glammify a non-exclusive, worldwide, royalty-free license to:</p>
                <ul>
                  <li>Process, modify, and transform the content for the purpose of providing the Services.</li>
                  <li>Store and manage the content as required to operate the platform.</li>
                </ul>

                <h6>c. Platform Rights for Improvement and Operations</h6>
                <p>To operate and improve the platform, you grant Glammify a non-exclusive, worldwide, royalty-free license to process both User Content and AI-generated outputs for purposes including:</p>
                <ul>
                  <li>System diagnostics and debugging</li>
                  <li>Quality assurance</li>
                  <li>Analytics and usage analysis</li>
                  <li>Safety monitoring</li>
                  <li>Model evaluation</li>
                  <li>Platform improvement</li>
                </ul>
                <p>Glammify does not intentionally distribute identifiable user content to other customers.</p>

                <h6>d. Rights for AI Training &amp; Promotion</h6>
                <p>You grant Glammify a <span className='legal-bold'>perpetual, irrevocable, worldwide, royalty-free license</span> to:</p>
                <ul>
                  <li>Use uploaded content and AI-generated outputs to train, test, and improve AI models.</li>
                  <li>Display generated outputs and non-sensitive content for marketing and promotional purposes including:
                    <ul>
                      <li>Website examples</li>
                      <li>Case studies</li>
                      <li>Social media posts</li>
                      <li>Demo videos</li>
                      <li>Product demonstrations</li>
                      <li>Sales materials</li>
                      <li>Investor presentations</li>
                    </ul>
                  </li>
                </ul>
                <p>This right survives termination of your account.</p>
              </div>

              {/* 4. Customer Model Upload & Likeness Consent */}
              <div className='col-md-12 legal-text'>
                <h3>4. Customer Model Upload &amp; Likeness Consent</h3>
                <p>The Services may allow you to upload images of real individuals ("Customer Models") to generate AI-assisted visuals.</p>
                <p>By uploading such images, you represent and warrant that:</p>
                <ul>
                  <li>You have obtained explicit consent from the individual depicted.</li>
                  <li>The individual has agreed to the AI processing and transformation of their likeness.</li>
                  <li>The content does not violate privacy rights, publicity rights, or applicable laws.</li>
                </ul>
                <p>Glammify does not verify the existence or validity of such consent and relies entirely on the representations made by the user.</p>
                <p>You are solely responsible for obtaining and maintaining appropriate consent.</p>
              </div>

              {/* 5. AI-Generated Content Disclaimer */}
              <div className='col-md-12 legal-text'>
                <h3>5. AI-Generated Content Disclaimer</h3>
                <p>The Services use artificial intelligence systems to generate images and videos based on your inputs.</p>
                <p>AI-generated outputs may unintentionally resemble real individuals, including celebrities or public figures. Any such resemblance is <span className='legal-bold'>coincidental and not intentional</span>.</p>
                <p>Glammify does not claim responsibility for such resemblance.</p>
                <p>Users are responsible for reviewing generated outputs before public or commercial use.</p>
              </div>

              {/* 6. AI Transformation Notice */}
              <div className='col-md-12 legal-text'>
                <h3>6. AI Transformation Notice</h3>
                <p>The Services generate synthetic visual outputs using machine learning systems.</p>
                <p>Generated outputs may transform or reinterpret uploaded inputs and may not exactly replicate the original content, jewelry products, or individuals.</p>
                <p>Outputs are AI-rendered visuals and should not be interpreted as exact photographic representations.</p>
              </div>

              {/* 7. Prohibited Uses */}
              <div className='col-md-12 legal-text'>
                <h3>7. Prohibited Uses</h3>
                <p>You agree not to:</p>
                <ul>
                  <li>Upload unlawful, infringing, defamatory, obscene, or hateful content.</li>
                  <li>Use the Services for illegal activities.</li>
                  <li>Create deceptive or misleading content.</li>
                  <li>Generate unauthorized deepfakes or impersonations.</li>
                  <li>Upload images of individuals without their consent.</li>
                  <li>Attempt to reverse engineer or exploit Glammify's AI systems.</li>
                </ul>
              </div>

              {/* 8. Intellectual Property of Glammify */}
              <div className='col-md-12 legal-text'>
                <h3>8. Intellectual Property of Glammify</h3>
                <p>All rights in the Services, including:</p>
                <ul>
                  <li>AI models</li>
                  <li>Platform software</li>
                  <li>Interface design</li>
                  <li>Algorithms</li>
                  <li>Trademarks</li>
                  <li>Branding</li>
                </ul>
                <p>are owned by Glammify and its licensors. You may not copy, modify, distribute, or reverse engineer the platform without written permission.</p>
              </div>

              {/* 9. Jewelry Design and Intellectual Property Responsibility */}
              <div className='col-md-12 legal-text'>
                <h3>9. Jewelry Design and Intellectual Property Responsibility</h3>
                <ul>
                  <li>Users are responsible for ensuring that jewelry designs, branding elements, or product materials uploaded to the platform are owned by them or used with authorization.</li>
                  <li>Glammify does not verify the originality or ownership of jewelry designs submitted by users.</li>
                  <li>Users agree not to use the Services to replicate proprietary designs belonging to competing brands.</li>
                  <li>Glammify shall not be responsible for intellectual property disputes related to user-submitted designs.</li>
                </ul>
              </div>

              {/* 10. Product Representation Disclaimer */}
              <div className='col-md-12 legal-text'>
                <h3>10. Product Representation Disclaimer</h3>
                <p>AI-generated visuals are intended for creative marketing and visualization purposes.</p>
                <p>Generated outputs may differ from the actual product in appearance, including differences in:</p>
                <ul>
                  <li>Lighting</li>
                  <li>Gemstone reflections</li>
                  <li>Colors</li>
                  <li>Surface textures</li>
                  <li>Styling context</li>
                </ul>
                <p>Users remain responsible for ensuring that marketing materials accurately represent their products.</p>
              </div>

              {/* 11. AI System Evolution */}
              <div className='col-md-12 legal-text'>
                <h3>11. AI System Evolution</h3>
                <p>Glammify continuously improves its artificial intelligence systems.</p>
                <p>As models and platform capabilities evolve, generated outputs may vary across different generations, platform versions, or updates.</p>
                <p>Glammify does not guarantee identical outputs across time.</p>
              </div>

              {showWhatsAppSmsAuth && (
                <div className='col-md-12 legal-text'>
                  <h3>12. Payment, {FlutterServicesAPI.getCreditLabel()} &amp; Refund Policy</h3>
                  <ul>
                    <li>{FlutterServicesAPI.getCreditLabel()} purchased on the platform are <span className='legal-bold'>non-refundable</span> except where required by law.</li>
                    <li>All fees are payable in the currency specified and are exclusive of applicable taxes.</li>
                    <li>AI-generated outputs may occasionally contain deviations. Users may contact support to request {FlutterServicesAPI.getCreditLabel().toLowerCase()} refunds.</li>
                    <li>Approval of refund requests is at the <span className='legal-bold'>sole discretion of Glammify</span>, and such decisions will be final and binding.</li>
                  </ul>
                </div>
              )}

              {/* 13. Limitation of Liability */}
              <div className='col-md-12 legal-text'>
                <h3>13. Limitation of Liability</h3>
                <p>To the fullest extent permitted by law:</p>
                <p>Glammify will not be liable for:</p>
                <ul>
                  <li>Indirect damages</li>
                  <li>Incidental damages</li>
                  <li>Loss of business</li>
                  <li>Loss of profits</li>
                  <li>Reputational damages</li>
                </ul>
                <p>Glammify's total liability will not exceed the amount paid by the user during the three months preceding the claim.</p>
              </div>

              {/* 14. Indemnification */}
              <div className='col-md-12 legal-text'>
                <h3>14. Indemnification</h3>
                <p>You agree to defend, indemnify, and hold harmless Glammify and its affiliates from any claims arising from:</p>
                <ul>
                  <li>Your uploaded content</li>
                  <li>Misuse of the Services</li>
                  <li>Violation of intellectual property rights</li>
                  <li>Violations of privacy or publicity rights</li>
                  <li>Unlawful marketing or content usage</li>
                </ul>
              </div>

              {/* 15. Termination */}
              <div className='col-md-12 legal-text'>
                <h3>15. Termination</h3>
                <p>Glammify may suspend or terminate accounts if:</p>
                <ul>
                  <li>These Terms are violated</li>
                  <li>Misuse of the Services is detected</li>
                  <li>Required by law</li>
                  <li>Platform risk is identified</li>
                </ul>
                <p>Termination may occur without prior notice.</p>
              </div>

              {/* 16. Governing Law & Jurisdiction */}
              <div className='col-md-12 legal-text'>
                <h3>16. Governing Law &amp; Jurisdiction</h3>
                <p>These Terms shall be governed by the laws of India.</p>
                <p>Any disputes shall be subject to the exclusive jurisdiction of the courts located in Mumbai, Maharashtra, India.</p>
              </div>

              {/* 17. Changes to Terms */}
              <div className='col-md-12 legal-text'>
                <h3>17. Changes to Terms</h3>
                <p>Glammify may update these Terms from time to time.</p>
                <p>Updates will become effective once published on the website or application.</p>
                <p>Continued use of the Services after updates constitutes acceptance of the revised Terms.</p>
              </div>

              {/* 18. Contact Information */}
              <div className='col-md-12 legal-text'>
                <h3>18. Contact Information</h3>
                <p>For questions regarding these Terms:</p>
                <ul>
                  <li>Email: <a href='mailto:support@glammify.ai'>support@glammify.ai</a></li>
                  {showWhatsAppSmsAuth && (
                    <li>Phone: <a href='tel:+918657992211'>+91 86579 92211</a></li>
                  )}
                  <li>Address: Crystal Plaza, 604/605, Guru Hargovindji Rd, next to Atul Projects, Chakala, Andheri East, Mumbai, Maharashtra 400099</li>
                </ul>
              </div>

            </div>
          </div>
        </div>
      </div>

      {showWhatsAppSmsAuth && <Footer />}
    </div>
  );
};

export default TermsPage;
